import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@hero/hooks-shared';
import { useLogoutManager, useSwitchOrgManager } from '@hero/web-hooks';
import { useFeature } from '@hero/web-context';
import {
  BuildingIcon,
  CheckIcon,
  ChevronDownIcon,
  LogOutIcon,
  UserIcon,
} from '@hero/web-icons';
import { isIdMatch } from '@hero/schema';
import type { MemberType, User } from '@hero/schema';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../ui/DropdownMenu/DropdownMenu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../ui/AlertDialog/AlertDialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../../ui/Avatar/Avatar';
import { cn } from '@hero/web-lib';
import { buttonVariants } from '../../../ui/Button/Button';
import { LanguageSelectorSubmenu } from '../../../app/LanguageSelector';

interface UserProfileMenuProps {
  className?: string;
}

type OrgEntry = User['orgs'][number];

/**
 * Collapse the switcher (show a single toggle row) once the user has
 * this many *other* orgs. Fewer orgs stay expanded — no extra click.
 */
const ORG_COLLAPSE_THRESHOLD = 3;

/**
 * Cap the expanded list height so Language / Logout stay on-screen.
 */
const ORG_LIST_MAX_HEIGHT_CLASS = 'max-h-[240px]';

/**
 * `memberType` labels rendered on the org-switcher rows. Keys match
 * `@hero/schema` `MemberType`; the `driver` bucket ships even though
 * dashboards don't render a driver-org member row today, so the map
 * stays exhaustive as the feature evolves.
 */
const MEMBER_TYPE_KEYS: Record<MemberType, string> = {
  admin: 'm-user:switch_org.member_type.admin',
  dispatcher: 'm-user:switch_org.member_type.dispatcher',
  driver: 'm-user:switch_org.member_type.driver',
  employee: 'm-user:switch_org.member_type.employee',
};

export const UserProfileMenu = ({ className }: UserProfileMenuProps) => {
  const { t } = useTranslation(['c-main', 'm-user']);
  const {
    user,
    name,
    phone,
    orgName,
    currentOrg,
    isFreelancerDriver,
    isIndividualCustomer,
    isLoading,
  } = useCurrentUser();
  const { logout, isLoggingOut } = useLogoutManager();
  const { switchOrg, isSwitching } = useSwitchOrgManager();
  const { isEnabled: canSignOut } = useFeature('auth_module', 'sub_sign_out');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [orgListExpanded, setOrgListExpanded] = useState(false);

  const orgs = user?.orgs ?? [];

  // "Other orgs" the user can flip to — everything except the current
  // one. If the user only has one org (or none), the switcher section
  // is skipped entirely.
  const otherOrgs = useMemo<OrgEntry[]>(() => {
    if (!currentOrg) return orgs;
    return orgs.filter(o => !isIdMatch(o._id, currentOrg._id));
  }, [orgs, currentOrg]);

  const useCollapsedSwitcher = otherOrgs.length >= ORG_COLLAPSE_THRESHOLD;
  const showOrgList = !useCollapsedSwitcher || orgListExpanded;

  // "Continue as individual" is a valid destination when the user has
  // an individual-side identity (freelancer driver or individual
  // customer) AND is currently signed into an Org — flipping into the
  // individual context requires the switch API to be told to allow a
  // sign-in without an Org.
  const hasIndividualIdentity = isFreelancerDriver || isIndividualCustomer;
  const showIndividualRow = hasIndividualIdentity && !!currentOrg;

  const showSwitcher = otherOrgs.length > 0 || showIndividualRow;

  const initials = name
    ? name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  const displayName = name || orgName || '';

  const handleMenuOpenChange = (open: boolean) => {
    setMenuOpen(open);
    if (!open) setOrgListExpanded(false);
  };

  const handleSwitchOrg = async (orgId: OrgEntry['_id']) => {
    if (isSwitching) return;
    try {
      await switchOrg(orgId);
    } catch {
      // toast is already surfaced by useSwitchOrgManager
    }
  };

  const handleContinueAsIndividual = async () => {
    if (isSwitching) return;
    try {
      await switchOrg(undefined, true);
    } catch {
      // toast is already surfaced by useSwitchOrgManager
    }
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
        <DropdownMenuTrigger
          className={cn(
            'flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none',
            className,
          )}
          disabled={isLoading}>
          <Avatar className="size-[40px]">
            <AvatarImage src="/images/avatar.png" alt="avatar" />
            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="text-sm text-start hidden md:block max-w-[140px]">
            <div className="font-medium text-gray-900 truncate" title={displayName}>
              {displayName}
            </div>
            {(orgName || phone) && (
              <div className="text-gray-500 text-xs truncate" title={orgName || phone}>
                {orgName || phone}
              </div>
            )}
          </div>
          <ChevronDownIcon className="size-4 text-gray-400 hidden sm:block" />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-72 p-0">
          <div className="p-1">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium truncate" title={displayName}>
                  {displayName}
                </p>
                {phone && <p className="text-xs text-muted-foreground truncate">{phone}</p>}
                {orgName && (
                  <p className="text-xs text-muted-foreground truncate" title={orgName}>
                    {orgName}
                  </p>
                )}
              </div>
            </DropdownMenuLabel>
          </div>

          {showSwitcher && (
            <>
              <DropdownMenuSeparator className="my-0" />
              <div className="p-1">
                {useCollapsedSwitcher && (
                  <DropdownMenuItem
                    onSelect={e => {
                      e.preventDefault();
                      setOrgListExpanded(prev => !prev);
                    }}
                    className="cursor-pointer">
                    <BuildingIcon className="size-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        {t('m-user:switch_org.section_title')}
                      </div>
                      {!orgListExpanded && (
                        <div className="text-xs text-muted-foreground">
                          {t('m-user:switch_org.other_count', { count: otherOrgs.length })}
                        </div>
                      )}
                    </div>
                    <ChevronDownIcon
                      className={cn(
                        'size-4 text-muted-foreground transition-transform',
                        orgListExpanded && 'rotate-180',
                      )}
                    />
                  </DropdownMenuItem>
                )}

                {!useCollapsedSwitcher && (
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                    {t('m-user:switch_org.section_title')}
                  </DropdownMenuLabel>
                )}
              </div>

              {showOrgList && (
                <>
                  <div
                    className={cn(
                      'overflow-y-auto overscroll-contain px-1',
                      useCollapsedSwitcher && ORG_LIST_MAX_HEIGHT_CLASS,
                    )}>
                    {currentOrg && (
                      <DropdownMenuItem disabled className="cursor-default opacity-100">
                        <BuildingIcon className="size-4 text-primary" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate" title={currentOrg.name}>
                            {currentOrg.name}
                          </div>
                          {currentOrg.memberType && (
                            <div className="text-xs text-muted-foreground">
                              {t(MEMBER_TYPE_KEYS[currentOrg.memberType])}
                            </div>
                          )}
                        </div>
                        <CheckIcon className="size-4 text-primary" />
                      </DropdownMenuItem>
                    )}
                    {otherOrgs.map(org => (
                      <DropdownMenuItem
                        key={String(org._id)}
                        onSelect={e => {
                          e.preventDefault();
                          void handleSwitchOrg(org._id);
                        }}
                        disabled={isSwitching}
                        className="cursor-pointer">
                        <BuildingIcon className="size-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate" title={org.name}>
                            {org.name}
                          </div>
                          {org.memberType && (
                            <div className="text-xs text-muted-foreground">
                              {t(MEMBER_TYPE_KEYS[org.memberType])}
                            </div>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </div>

                  {showIndividualRow && (
                    <div className="p-1">
                      <DropdownMenuItem
                        onSelect={e => {
                          e.preventDefault();
                          void handleContinueAsIndividual();
                        }}
                        disabled={isSwitching}
                        className="cursor-pointer">
                        <UserIcon className="size-4 text-muted-foreground" />
                        <span className="text-sm">
                          {t('m-user:switch_org.continue_as_individual')}
                        </span>
                      </DropdownMenuItem>
                    </div>
                  )}
                </>
              )}

              {!showOrgList && showIndividualRow && (
                <div className="p-1">
                  <DropdownMenuItem
                    onSelect={e => {
                      e.preventDefault();
                      void handleContinueAsIndividual();
                    }}
                    disabled={isSwitching}
                    className="cursor-pointer">
                    <UserIcon className="size-4 text-muted-foreground" />
                    <span className="text-sm">
                      {t('m-user:switch_org.continue_as_individual')}
                    </span>
                  </DropdownMenuItem>
                </div>
              )}
            </>
          )}

          <DropdownMenuSeparator className="my-0" />

          <div className="p-1">
            <LanguageSelectorSubmenu label={t('c-main:profile_menu.language')} />

            {canSignOut && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => setShowLogoutConfirm(true)}
                  disabled={isLoggingOut}
                  className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50">
                  <LogOutIcon className="size-4" />
                  <span>{t('c-main:profile_menu.logout')}</span>
                </DropdownMenuItem>
              </>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('c-main:profile_menu.logout_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('c-main:profile_menu.logout_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoggingOut}>
              {t('c-main:profile_menu.logout_cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => logout()}
              disabled={isLoggingOut}
              className={buttonVariants({ variant: 'destructive' })}>
              {t('c-main:profile_menu.logout_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
